import { memo, useMemo } from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import OrderSectionPopoverContent from 'components/popover/OrderSectionPopoverContent';
import { TRADE_PAGE_VERIFICATION_FLAGS } from 'constants/enums';
import {
  GRACE_KYC_EXPIRY_AVAILABLE,
  RENEW_KYC_SELFIE_VERIFICATION_ENABLED,
} from 'constants/featureFlag';
import { REKYC_VERIFICATION_SOURCES } from 'constants/mixpanelEventNames';
import routes from 'constants/routes';
import {
  isKycRequiredDeadlineCrossed,
  isUserNonIndianAndReduceOnly,
  noop,
} from 'helpers/utils';
import { useRekycVerificationNavigation } from 'hooks/componentHooks/useRekycVerificationNavigation';
import useTotalAccountEquity from 'hooks/componentHooks/useTotalAccountEquity';
import useTradeTabMobile from 'hooks/useTradeTabMobile';
import i18n from 'i18n/config';
import { userSelector } from 'selectors/otherSelectors';
import { selectTradePagesVerificationMessageFlag } from 'selectors/user';
import { useAppSelector } from 'storeHooks';
import DeltaButton from 'UIKit/DeltaButton';
import PopoverWithCustomIcon from 'UIKit/PopoverWithCustomIcon';

import styles from './kycRefresh.module.scss';

const renewKycToTrade = i18n.t('trading:renewKycToTrade');

const reduceOnlyMode = i18n.t('trading:reduceOnlyMode');

const renewKycText = i18n.t('trading:renewKyc');
const KycRefreshExpiredContent = ({
  buttonLabel,
  textLabel,
  isTradeTabMobile,
}: {
  buttonLabel?: string;
  textLabel: string;
  isTradeTabMobile?: boolean;
}) => {
  const { navigateToVerification } = useRekycVerificationNavigation(
    REKYC_VERIFICATION_SOURCES.REDUCE_ONLY_STRIP
  );

  return (
    (<div
      className={cn(styles.kycRefreshContainer, {
        [styles.inTradeTabMobile_expireKycRefreshContainer]: isTradeTabMobile,
      })}
      data-palette="KycRefreshExpiredContent">
      <div className={styles.reduceOnlyText}>
        <PopoverWithCustomIcon
          iconComponent={<p className={styles.stripText} data-palette="KycRefreshExpiredContent">{textLabel}</p>}
          popoverComponent={
            <OrderSectionPopoverContent
              text={<p data-palette="KycRefreshExpiredContent">{i18n.t('trading:beforeKycMessageTooltipText')}</p>}
            />
          }
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        />
      </div>
      {RENEW_KYC_SELFIE_VERIFICATION_ENABLED ? (
        <DeltaButton
          data-testid="reduce-only-mode-place-order-button"
          tabIndex={0}
          onKeyPress={noop}
          onClick={navigateToVerification}
          type="button"
          variant="primary-t4"
        >
          {renewKycText}
        </DeltaButton>
      ) : (
        <a
          href={`/app${routes.account.identityVerification}`}
          data-testid="reduce-only-mode-link"
          data-palette="KycRefreshExpiredContent">
          <DeltaButton
            data-testid="reduce-only-mode-place-order-button"
            tabIndex={0}
            onKeyPress={noop}
            type="button"
            variant="primary-t4"
          >
            {buttonLabel}
          </DeltaButton>
        </a>
      )}
    </div>)
  );
};

const KycRefreshExpired = () => {
  const { t } = useTranslation(['trading']);

  const user = useAppSelector(userSelector);

  const flag = useAppSelector(selectTradePagesVerificationMessageFlag);

  const totalBalance = useTotalAccountEquity();

  const isKycRequired = useMemo(() => isKycRequiredDeadlineCrossed(user), [user]);
  const isStartKyc = useMemo(
    () => isUserNonIndianAndReduceOnly(user, totalBalance),
    [user, totalBalance]
  );
  const isTradeTabMobile = useTradeTabMobile();

  const buttonLabel = useMemo(() => {
    if (isKycRequired) {
      return t('trading:initiateKyc');
    }
    if (isStartKyc) {
      return t('trading:startKyc');
    }
    return t('trading:verifyNow');
  }, [isKycRequired, isStartKyc, t]);

  if (GRACE_KYC_EXPIRY_AVAILABLE && flag !== null) {
    switch (flag) {
      case TRADE_PAGE_VERIFICATION_FLAGS.REDUCE_ONLY:
        return (
          <KycRefreshExpiredContent
            buttonLabel={buttonLabel}
            textLabel={reduceOnlyMode}
            isTradeTabMobile={isTradeTabMobile}
          />
        );
      case TRADE_PAGE_VERIFICATION_FLAGS.RENEW_KYC:
        return (
          <KycRefreshExpiredContent
            buttonLabel={buttonLabel}
            textLabel={renewKycToTrade}
            isTradeTabMobile={isTradeTabMobile}
          />
        );

      default:
        return null;
    }
  }

  return (
    <KycRefreshExpiredContent
      textLabel={reduceOnlyMode}
      buttonLabel={buttonLabel}
      isTradeTabMobile={isTradeTabMobile}
    />
  );
};

export default memo(KycRefreshExpired);
